import type { InsightsKpiKind, InsightsTrendPoint } from '@ucp/web/lib';

/**
 * PII-safe KPI value formatting (PRO-0590). Pure, deterministic; used by both the
 * headline value and the accessible data-table (NF-10) so they never drift.
 */
export function formatKpiValue(value: number, kind: InsightsKpiKind): string {
  switch (kind) {
    case 'currency_gbp':
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
        maximumFractionDigits: 0,
      }).format(value);
    case 'percentage':
      return `${new Intl.NumberFormat('en-GB', { maximumFractionDigits: 1 }).format(value)}%`;
    case 'nps':
      // NPS is a signed index (-100…100); a leading + reads as a score, not a count.
      return value > 0 ? `+${value}` : `${value}`;
    case 'count':
    default:
      return new Intl.NumberFormat('en-GB').format(value);
  }
}

/**
 * Compute a "nice"-rounded, data-driven Y-axis domain so a series that floats in a
 * narrow band (e.g. School Occupancy at 88–91%) fills the chart instead of hugging
 * the top of a fixed 0→100 axis (PRO-0590 follow-up). Pure and deterministic.
 *
 * This is a LINE-chart affordance only — bar charts must keep a zero baseline (a
 * truncated bar exaggerates differences; DV-1 honest-representation floor). The
 * domain is clamped to kind-aware absolute bounds (percentage never exceeds 0–100,
 * NPS is a signed −100…100 index, counts/currency never go below 0), and always
 * leaves at least one step of headroom above and below the data.
 */
export function computeTrendDomain(
  trend: readonly InsightsTrendPoint[],
  kind: InsightsKpiKind,
): [number, number] {
  const values = trend.map((point) => point.value).filter((value) => Number.isFinite(value));
  if (values.length === 0) {
    return [0, 1];
  }

  const dataMin = Math.min(...values);
  const dataMax = Math.max(...values);

  const hardFloor = kind === 'nps' ? -100 : 0;
  const hardCeil = kind === 'percentage' || kind === 'nps' ? 100 : Number.POSITIVE_INFINITY;

  let span = dataMax - dataMin;
  if (span <= 0) {
    // Flat series: synthesise a band around the single value so it doesn't collapse
    // onto the axis line.
    span = Math.max(Math.abs(dataMax) * 0.1, 1);
  }

  const step = niceStep(span);
  let lower = Math.floor(dataMin / step) * step;
  let upper = Math.ceil(dataMax / step) * step;

  // Guarantee visible headroom when the data sits exactly on a step boundary.
  if (lower === dataMin) {
    lower -= step;
  }
  if (upper === dataMax) {
    upper += step;
  }

  lower = Math.max(lower, hardFloor);
  upper = Math.min(upper, hardCeil);

  // Degenerate guard: if the clamp collapsed the band, re-open it by one step.
  if (upper <= lower) {
    upper = Math.min(lower + step, hardCeil);
    if (upper <= lower) {
      lower = Math.max(upper - step, hardFloor);
    }
  }

  return [roundAxis(lower), roundAxis(upper)];
}

/** A rounded axis interval (~4 divisions) drawn from the 1/2/5 × 10ⁿ family. */
function niceStep(span: number): number {
  const rough = span / 4;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const normalised = rough / magnitude;
  const factor = normalised < 1.5 ? 1 : normalised < 3 ? 2 : normalised < 7 ? 5 : 10;
  return factor * magnitude;
}

/** Trim binary-float noise so axis bounds stay clean (e.g. 96.39999 → 96.4). */
function roundAxis(value: number): number {
  return Math.round(value * 1000) / 1000;
}

/** A coarse period label (e.g. `2026-06`) rendered as a short month for axes/tables. */
export function formatPeriod(period: string, variant: 'table' | 'axis' = 'table'): string {
  const match = /^(\d{4})-(\d{2})$/.exec(period);
  if (!match) {
    return period;
  }
  const [, year, month] = match;
  const date = new Date(Number(year), Number(month) - 1, 1);
  if (Number.isNaN(date.getTime())) {
    return period;
  }
  // Axis ticks drop the year so six monthly labels fit a phone-width plot; the
  // data-table equivalent keeps the year (NF-10 — table remains authoritative).
  return new Intl.DateTimeFormat('en-GB', {
    month: 'short',
    ...(variant === 'table' ? { year: 'numeric' as const } : {}),
  }).format(date);
}

/**
 * Compact Y-axis tick for the decorative Recharts canvas. Headline values and the
 * accessible data table keep `formatKpiValue` (full figures); ticks only need to
 * orient the plot, and raw millions overflow a ~390px card.
 */
export function formatKpiAxisTick(value: number, kind: InsightsKpiKind): string {
  switch (kind) {
    case 'currency_gbp':
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
        notation: 'compact',
        compactDisplay: 'short',
        maximumFractionDigits: 1,
      }).format(value);
    case 'percentage':
      return `${new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 }).format(value)}%`;
    case 'nps':
      return value > 0 ? `+${Math.round(value)}` : `${Math.round(value)}`;
    case 'count':
    default:
      return new Intl.NumberFormat('en-GB', {
        notation: 'compact',
        compactDisplay: 'short',
        maximumFractionDigits: 1,
      }).format(value);
  }
}
